import { useId } from 'react';
import clsx from 'clsx';

import { formatUsd } from '../lib/format';

/**
 * Dependency-free SVG charts tuned for the walrus-navy cabinet. Hand-
 * rolled (no recharts/visx) so we fully control the aesthetic — gradient
 * area fills, the brand accent stroke, soft glow — and add zero bundle
 * weight.
 */

function buildPath(
  values: number[],
  width: number,
  height: number,
  pad = 2,
): { line: string; area: string } {
  if (values.length === 0) return { line: '', area: '' };
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const stepX = values.length > 1 ? (width - pad * 2) / (values.length - 1) : 0;
  const pts = values.map((v, i) => {
    const x = pad + i * stepX;
    const y = pad + (height - pad * 2) * (1 - (v - min) / span);
    return [x, y] as const;
  });
  const line = pts
    .map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`)
    .join(' ');
  const area =
    `${line} L${pts[pts.length - 1][0].toFixed(2)},${height} ` +
    `L${pts[0][0].toFixed(2)},${height} Z`;
  return { line, area };
}

/** Area chart with gradient fill — the equity-curve workhorse. */
export function AreaChart({
  values,
  height = 120,
  className,
  positive,
}: {
  values: number[];
  height?: number;
  className?: string;
  positive?: boolean;
}) {
  const id = useId().replace(/:/g, '');
  const width = 600; // viewBox units; scales via preserveAspectRatio none
  const { line, area } = buildPath(values, width, height);
  const stroke = positive === false ? 'var(--pb-danger)' : 'var(--pb-accent)';
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className={clsx('w-full', className)}
      style={{ height }}
      aria-hidden
    >
      <defs>
        <linearGradient id={`fill-${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={stroke} stopOpacity="0.28" />
          <stop offset="100%" stopColor={stroke} stopOpacity="0" />
        </linearGradient>
      </defs>
      {area ? <path d={area} fill={`url(#fill-${id})`} /> : null}
      {line ? (
        <path
          d={line}
          fill="none"
          stroke={stroke}
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      ) : null}
    </svg>
  );
}

/** Tiny inline sparkline for stat cards / position rows. */
export function Sparkline({
  values,
  width = 96,
  height = 28,
  positive,
  className,
}: {
  values: number[];
  width?: number;
  height?: number;
  positive?: boolean;
  className?: string;
}) {
  const { line } = buildPath(values, width, height, 1);
  const stroke =
    positive === undefined
      ? 'var(--pb-accent-2)'
      : positive
        ? 'var(--pb-success)'
        : 'var(--pb-danger)';
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={className}
      aria-hidden
    >
      {line ? (
        <path
          d={line}
          fill="none"
          stroke={stroke}
          strokeWidth="1.75"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      ) : null}
    </svg>
  );
}

/**
 * Polymarket-style price chart: the underlying asset price over the
 * epoch with a dashed "Price to beat" target line, a pulsing dot at the
 * live price, and right-axis price ticks. The whole y-range is scaled to
 * include the target so the line is always visible.
 */
export function PmPriceChart({
  values,
  target,
  targetLabel,
  height = 200,
}: {
  values: number[];
  target: number;
  targetLabel: string;
  height?: number;
}) {
  const id = useId().replace(/:/g, '');
  const width = 600;
  if (values.length < 2) {
    return <div style={{ height }} className="rounded-pb-sm bg-pb-paper2/30" />;
  }
  const all = [...values, target];
  const min = Math.min(...all);
  const max = Math.max(...all);
  const span = max - min || 1;
  const lo = min - span * 0.14;
  const hi = max + span * 0.14;
  const range = hi - lo;
  const yOf = (v: number) => ((hi - v) / range) * height;
  const xStep = width / (values.length - 1);
  const pts = values.map((v, i) => [i * xStep, yOf(v)] as const);
  const line = pts
    .map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`)
    .join(' ');
  const area = `${line} L${width},${height} L0,${height} Z`;
  const last = values[values.length - 1];
  const STROKE = '#f0a04b'; // burnt-honey amber — matches the PM widget
  const targetPct = (yOf(target) / height) * 100;
  const lastPct = (yOf(last) / height) * 100;

  return (
    <div className="relative w-full" style={{ height }}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        className="h-full w-full"
        aria-hidden
      >
        <defs>
          <linearGradient id={`pm-${id}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={STROKE} stopOpacity="0.24" />
            <stop offset="100%" stopColor={STROKE} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={area} fill={`url(#pm-${id})`} />
        <line
          x1="0"
          y1={yOf(target)}
          x2={width}
          y2={yOf(target)}
          stroke="var(--pb-text-hint)"
          strokeWidth="1"
          strokeDasharray="6 5"
          vectorEffect="non-scaling-stroke"
        />
        <path
          d={line}
          fill="none"
          stroke={STROKE}
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>

      {/* right-axis ticks */}
      <span className="pb-numeral absolute right-1 top-0 text-[10px] text-tg-hint">
        {formatUsd(hi)}
      </span>
      <span className="pb-numeral absolute bottom-0 right-1 text-[10px] text-tg-hint">
        {formatUsd(lo)}
      </span>

      {/* target pill on the dashed line */}
      <span
        className="pb-numeral absolute right-1 -translate-y-1/2 rounded-pb-sm bg-pb-paper2 px-1.5 py-0.5 text-[10px] font-medium text-tg-hint"
        style={{ top: `${targetPct}%` }}
      >
        {targetLabel}
      </span>

      {/* live price dot */}
      <span
        className="absolute right-0 -translate-y-1/2"
        style={{ top: `${lastPct}%` }}
      >
        <span className="relative flex h-3 w-3">
          <span
            className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-60"
            style={{ background: STROKE }}
          />
          <span
            className="relative inline-flex h-3 w-3 rounded-full ring-2 ring-tg-bg"
            style={{ background: STROKE }}
          />
        </span>
      </span>
    </div>
  );
}

export interface DonutSegment {
  value: number;
  color: string;
  label: string;
}

/** Allocation donut — animated stroke segments, centered total slot. */
export function Donut({
  segments,
  size = 160,
  thickness = 18,
  center,
}: {
  segments: DonutSegment[];
  size?: number;
  thickness?: number;
  center?: React.ReactNode;
}) {
  const r = (size - thickness) / 2;
  const c = 2 * Math.PI * r;
  const total = segments.reduce((a, s) => a + Math.max(0, s.value), 0) || 1;
  let offset = 0;
  return (
    <div className="relative inline-grid place-items-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90" aria-hidden>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--pb-border-soft)"
          strokeWidth={thickness}
        />
        {segments.map((s, i) => {
          const frac = Math.max(0, s.value) / total;
          const dash = frac * c;
          const el = (
            <circle
              key={i}
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke={s.color}
              strokeWidth={thickness}
              strokeDasharray={`${dash} ${c - dash}`}
              strokeDashoffset={-offset}
              strokeLinecap="round"
              style={{ transition: 'stroke-dasharray 600ms ease, stroke-dashoffset 600ms ease' }}
            />
          );
          offset += dash;
          return el;
        })}
      </svg>
      {center ? (
        <div className="absolute inset-0 grid place-items-center text-center">
          {center}
        </div>
      ) : null}
    </div>
  );
}
